import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Share2, Heart } from "lucide-react"
import { useState, useEffect } from "react"

const fallbackArticle = {
  id: 1,
  title: "Comment créer l'album photo parfait pour vos souvenirs",
  description: "Découvrez nos conseils d'experts pour créer un album photo qui raconte votre histoire de la plus belle façon.",
  categories: "Conseils",
  image: "/images/occasions/famille1.png",
  excerpt: "Découvrez nos conseils d'experts pour créer un album photo qui raconte votre histoire de la plus belle façon.",
  permalink: "comment-creer-album-photo-parfait",
  content: `
    <p>Créer un album photo parfait est un art qui allie technique et émotion. Vos souvenirs méritent d'être présentés de la plus belle façon possible, et nous allons vous guider étape par étape pour y parvenir.</p>

    <h2>1. La sélection des photos</h2>
    <p>La première étape cruciale consiste à sélectionner les bonnes photos. Ne gardez que les images qui racontent vraiment votre histoire. Privilégiez la qualité à la quantité - mieux vaut 50 photos exceptionnelles que 200 photos moyennes.</p>

    <h2>2. L'organisation chronologique</h2>
    <p>Organisez vos photos de manière chronologique ou thématique. Cette structure aide à créer un fil narratif cohérent qui guide le lecteur dans votre histoire.</p>

    <h2>3. Le choix du format</h2>
    <p>Selon l'occasion, choisissez le format approprié :</p>
    <ul>
      <li><strong>Format carré :</strong> Parfait pour les réseaux sociaux et un style moderne</li>
      <li><strong>Format paysage :</strong> Idéal pour les voyages et panoramas</li>
      <li><strong>Format portrait :</strong> Excellent pour les photos de famille</li>
    </ul>

    <h2>4. La mise en page</h2>
    <p>Variez les mises en page pour créer du dynamisme. Alternez entre pages avec une seule grande photo et pages avec plusieurs petites images. Laissez respirer vos photos avec des espaces blancs.</p>

    <h2>5. Les textes et légendes</h2>
    <p>N'hésitez pas à ajouter des légendes courtes et percutantes. Elles apportent du contexte et de l'émotion à vos images. Quelques mots suffisent parfois à faire revivre un moment.</p>

    <h2>Conclusion</h2>
    <p>Un album photo réussi raconte une histoire authentique. Prenez le temps de soigner chaque détail, car ces souvenirs imprimés traverseront les années et feront la joie des générations futures.</p>
  `,
  date: "15 Mars 2024",
  readTime: "5 min"
}

export default function BlogArticle() {
  const { id } = useParams()
  const [article, setArticle] = useState(fallbackArticle)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) {
        setArticle(fallbackArticle)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`/load_article/${id}`)
        if (response.ok) {
          const data = await response.json()
          setArticle(data.article)
        } else {
          setArticle(fallbackArticle)
        }
      } catch (error) {
        console.error('Error fetching article:', error)
        setArticle(fallbackArticle)
      } finally {
        setLoading(false)
      }
    }

    fetchArticle()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Chargement...
            </h1>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 lg:px-8 py-16">
        {/* Back button */}
        <div className="mb-8 mt-6">
          <Link to="/blog">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Button>
          </Link>
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto">
          <header className="mb-12 text-center">
            <h1 className="text-3xl md:text-5xl font-display font-bold mb-6 text-gradient">
              {article.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          {/* Featured Image */}
          <div className="mb-12">
            <img 
              src={article.image} 
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <div 
              dangerouslySetInnerHTML={{ __html: article.content }}
              className="space-y-6 text-muted-foreground leading-relaxed"
            />
          </div>
        </article>
      </main>
      <Footer />
    </div>
  )
}